/* eslint-disable no-nested-ternary */
import {
  takeLatest, put, call, select
} from 'redux-saga/effects';

import {
  merge, omit, compose, map,
  reject, isNil
} from 'ramda';
import dayjs from 'dayjs';

import Utils from 'src/utils';
import {
  worktimeTypesActions,
  worktimeTypeActions,
  workDayEventTypesActions,

  selectWorktimeTypesData
} from 'src/store/reducers/WorktimeTypesReducer';
import toastrError from 'src/helpers/toastr/toastrError';
import toastrSuccess from 'src/helpers/toastr/toastrSuccess';
import { wtsApi } from 'src/api';

// eslint-disable-next-line func-names
export default function* () {
  yield [
    takeLatest(worktimeTypesActions.request, worktimeTypesSaga),
    takeLatest(worktimeTypeActions.submit, worktimeTypeUpdateSaga),
    takeLatest(worktimeTypeActions.delete, worktimeTypeDeleteSaga),
    takeLatest(workDayEventTypesActions.request, workDayEventTypesSaga)
  ];
}

function* worktimeTypesSaga() {
  try {
    const rawResponse = yield call(wtsApi.workTimeCalculationTypes.workTimeCalculationTypesList);
    const response = rawResponse.data.Data;

    const list = compose(
      Utils.objects.normalizeList('id'),
      map(parseWorktimeType),
      map(Utils.objects.decapitalizeObject)
    )(response);

    yield put({
      type: worktimeTypesActions.success,
      payload: list
    });

    yield put({
      type: worktimeTypesActions.optionsSuccess,
      payload: response
    });

    // load workDayEventTypes options
    yield put({
      type: workDayEventTypesActions.request
    });
  } catch (e) {
    toastrError(e);
    yield put({
      type: worktimeTypesActions.fail,
      payload: e.message
    });
  }
}

function* worktimeTypeUpdateSaga({ payload }) {
  try {
    const { id, ...data } = payload;

    const request = id
      ? wtsApi.workTimeCalculationTypes.workTimeCalculationTypesUpdate
      : wtsApi.workTimeCalculationTypes.workTimeCalculationTypesCreate;
    const params = shapeWorktimeTypePostParams(id ? payload : data);

    const rawResponse = yield call(request, params);
    const response = rawResponse.data;
    const type = parseWorktimeType(Utils.objects.decapitalizeObject(response.Data));
    const list = yield select(selectWorktimeTypesData);

    const updatedList = merge(list, { [type.id]: type });

    yield put({
      type: worktimeTypesActions.success,
      payload: updatedList
    });

    yield put({
      type: worktimeTypesActions.optionsSuccess,
      payload: response
    });

    toastrSuccess(response);
  } catch (e) {
    toastrError(e);
  }
}

const parseWorktimeType = ({
  onlyPeriodTime,
  dayType,
  weekDays,
  ...other
}) => ({
  ...other,
  dayType: dayType ? dayType.toString().toLowerCase() : 'ALL',
  onlyPeriodTime: onlyPeriodTime ? 'true' : 'false',
  weekDays: (weekDays || []).map((item) => item.toString())
});

const shapeWorktimeTypePostParams = ({
  id: Id,
  name: Name,
  externalId: ExternalId,
  exportCode: ExportCode,
  dayType,
  weekDays,
  period,
  isActive: IsActive,
  onlyPeriodTime,
  jobIds: JobIds,
  type: Type,
  timeRateSystemId: TimeRateSystemId,
  splitByEventDepartment: SplitByEventDepartment,
  userIds: UserIds,
  workGroupIds: WorkGroupIds,
  subtrahendIds: SubtrahendIds,
  includeFlags: IncludeFlags,
  excludeFlags: ExcludeFlags
}) => {
  const {
    StartTime,
    EndTime
  } = period || {};

  return reject(isNil, {
    Id,
    Name,
    ExternalId,
    ExportCode,
    Period: period === null
      ? null
      : {
        StartTime: dayjs(StartTime || '00:00:00', 'HH:mm:ss').format('HH:mm:ss'),
        EndTime: dayjs(EndTime || '00:00:00', 'HH:mm:ss').format('HH:mm:ss')
      },
    IsActive,
    OnlyPeriodTime: onlyPeriodTime === 'true',
    JobIds: JobIds || [],
    SplitByEventDepartment,
    DayType: dayType !== 'ALL'
      ? dayType
      : null,
    WeekDays: !weekDays.includes('ALL')
      ? weekDays.map((item) => parseInt(item, 10))
      : [],
    Type,
    TimeRateSystemId,
    UserIds,
    WorkGroupIds,
    SubtrahendIds,
    IncludeFlags,
    ExcludeFlags
  });
};

function* worktimeTypeDeleteSaga({ payload: id }) {
  try {
    const rawResponse = yield call(wtsApi.workTimeCalculationTypes.workTimeCalculationTypesDelete, { id });
    const response = rawResponse.data;
    const list = yield select(selectWorktimeTypesData);

    const updatedList = omit([id], list);

    yield put({
      type: worktimeTypesActions.success,
      payload: updatedList
    });

    yield put({
      type: worktimeTypesActions.optionsSuccess,
      payload: response
    });

    toastrSuccess(response);
  } catch (e) {
    toastrError(e);
  }
}

function* workDayEventTypesSaga() {
  try {
    const typesResponse = yield call(wtsApi.workDayEventTypes.workDayEventTypesList);
    const types = typesResponse.data.Data;
    const typesArray = Object.keys(types || {})
      .reduce((acc, label) => {
        acc.push({ label, value: label.toLowerCase() });
        return acc;
      }, []);

    yield put({
      type: workDayEventTypesActions.success,
      payload: typesArray
    });
  } catch (e) {
    toastrError(e);
  }
}
